import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import luxPlusGlossaryMessages from 'i18n/luxPlusGlossaryMessages'
import LuxPlusPreferredPaymentMethod from '../LuxPlusPreferredPaymentMethod'
import LuxPlusPaymentMethods from 'luxPlus/components/LuxPlusPaymentMethods'
import LuxPlusRenewalPointsOptIn from 'luxPlus/components/LuxPlusRenewalPoints/LuxPlusRenewalPointsOptIn'
import TextLink from 'LuxKit/TextLink'
import moment from 'moment'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { connect } from 'react-redux'
import { getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import { getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import config from 'constants/config'

interface MappedProps {
  isPendingRenewalLuxPlusMember: boolean
  renewalDate?: string
}

interface Props extends MappedProps {
  expectedBackupPaymentMethodId?: string
  item: App.MemberSubscriptionItem
  onCancelMembership?: () => void
  onCancelRenew?: () => void
  onExpectedBackupPaymentMethodFound?: () => void
  onReactivateRenew?: () => void
  onUpdatePaymentMethod?: () => void
  onAddBackupPaymentMethod?: () => void
  paymentMethodsRefreshKey?: number
}

function LuxPlusManageAccountModalOverview(props: Props) {
  const {
    expectedBackupPaymentMethodId,
    item,
    onCancelMembership,
    onCancelRenew,
    onExpectedBackupPaymentMethodFound,
    onReactivateRenew,
    onUpdatePaymentMethod,
    onAddBackupPaymentMethod,
    paymentMethodsRefreshKey,
    renewalDate,
    isPendingRenewalLuxPlusMember,
  } = props

  const intl = useIntl()
  // The member's resolved renewal price — can differ from the catalog for grandfathered members.
  const yearlySubscriptionPrice = item.currentRenewalOffer?.price
  const hasAutoRenewal = item.renewalStatus === 'ALLOW_RENEWAL'
  const canCancel = useMemo(() => {
    const cancellationPeriodEndDate = moment(item.activeSubscription?.startDate).add(LUXURY_PLUS.REFUND_ELIGIBILITY_PERIOD_IN_DAYS, 'days').endOf('day')
    return !!item.activeSubscription &&
      item.activeSubscription.redeemedBenefits.length === 0 &&
      cancellationPeriodEndDate.isAfter(moment())
  }, [item])
  let paymentMethods = <LuxPlusPreferredPaymentMethod
    footer={<LuxPlusRenewalPointsOptIn />}
    item={item}
    onUpdatePaymentMethod={onUpdatePaymentMethod}
    renewalDate={renewalDate}
  />
  if (config.LUXURY_PLUS_BACKUP_PAYMENT_METHOD_CHECKOUT_ENABLED) {
    paymentMethods = <LuxPlusPaymentMethods
      preferredPaymentMethodFooter={<LuxPlusRenewalPointsOptIn />}
      expectedBackupPaymentMethodId={expectedBackupPaymentMethodId}
      item={item}
      onAddBackupPaymentMethod={onAddBackupPaymentMethod}
      onExpectedBackupPaymentMethodFound={onExpectedBackupPaymentMethodFound}
      onUpdatePaymentMethod={onUpdatePaymentMethod}
      refreshKey={paymentMethodsRefreshKey}
      renewalDate={renewalDate}
    />
  }

  return <>
    <ModalHeader title={intl.formatMessage(luxPlusGlossaryMessages.manageMembership)} />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={12}>
          {hasAutoRenewal && (item.activeSubscription || isPendingRenewalLuxPlusMember) && <>
            {!!renewalDate && <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">{intl.formatMessage({
                id: '4eP9Pffzk38RNqvrXkbP',
                defaultMessage: 'Renewal date',
                description: 'Label for the membership renewal date in the manage account modal',
              })}
              </BodyText>
              <BodyText variant="medium"><FormatDateTime value={renewalDate} dateStyle="medium" /></BodyText>
            </Group>}
            <Divider kind="primary" />
            <VerticalSpacer gap={4}>
              <Group direction="horizontal" horizontalAlign="space-between">
                <BodyText variant="medium" weight="bold">{intl.formatMessage({
                  id: 's6sBEoUbrgCye5hFqGR1',
                  defaultMessage: 'Annual membership fee',
                  description: 'Label for the annual membership fee in the manage account modal',
                })}
                </BodyText>
                <BodyText variant="medium"><FormatCurrency value={yearlySubscriptionPrice ?? item?.currentSubscriptionPrice ?? 0} /></BodyText>
              </Group>
              <BodyText variant="small">
                {intl.formatMessage({
                  id: '4B1ymwkBYaDiWP1S3Vyf',
                  defaultMessage: 'Amount will be automatically charged to your card on your renewal date',
                  description: 'Informational text about automatic charging for membership renewal',
                })}
              </BodyText>
            </VerticalSpacer>
            <Divider kind="primary" />
          </>}
          {paymentMethods}
          <Group direction="horizontal" horizontalAlign="end" gap={20}>
            {canCancel && <TextLink size="medium" onClick={onCancelMembership}>{intl.formatMessage({
              id: 'KLVQbAxCl6CTQekoe52C',
              defaultMessage: 'Cancel membership',
              description: 'Link text to cancel LuxPlus+ membership in the cancellation or refund flow',
            })}
            </TextLink>}
            {hasAutoRenewal && <TextLink size="medium" onClick={onCancelRenew}>{intl.formatMessage({
              id: 't7ueODj188KOY6eQ68rp',
              defaultMessage: 'Cancel renewal',
              description: 'Hyperlink text to cancel LuxPlus+ membership renewal',
            })}
            </TextLink>}
            {!hasAutoRenewal && <TextLink size="medium" onClick={onReactivateRenew}>{intl.formatMessage({
              id: '61-vFBm040Nf9iTiN8Qh',
              defaultMessage: 'Re-enable renewal',
              description: 'Link text to re-enable LuxPlus+ membership auto-renewal',
            })}
            </TextLink>}
          </Group>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  renewalDate: getLuxPlusSubscriptionRenewalDate(state),
  isPendingRenewalLuxPlusMember: getIsPendingRenewalLuxPlusMember(state),
})

export default connect(mapStateToProps)(LuxPlusManageAccountModalOverview)
